/**
 * *No map location* — `docs/property-maps-v2.md` §5.1 state 1, board 6 tile 1.
 *
 * A property with no coordinates is a property with nowhere to draw, so this
 * comes before every other state. Two ways out: search the address again
 * (Geocoding, through our own route so the key stays on the server), or drop a
 * pin by hand.
 *
 * **The pin is saved to the property**, not to the map — it is the same field
 * Navigate and the crew app's boundary read, which is why the panel says so.
 * Saving goes through the ordinary property update (§4.4), so there is one
 * writer of a property's coordinates and this screen is not a second one.
 */
import { useState } from 'react'
import { ApiError } from '@rowted/shared'
import { Button } from '../../components/primitives.js'
import { TextInput } from '../../components/form/inputs.js'
import { IconPin, IconSearch } from '../../frame/icons.js'
import { geocode, type GeocodeHit } from '../../lib/map-provider.js'
import { cn } from '../../lib/cn.js'
import { MapCanvasFrame, MapPanel, PanelActions } from './map-panel.js'

export function NoMapLocation({
  address, canEdit, onSave,
}: {
  address: string
  canEdit: boolean
  onSave: (at: { lng: number; lat: number }) => Promise<void>
}) {
  const [query, setQuery] = useState(address)
  const [hits, setHits] = useState<GeocodeHit[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  async function search() {
    setBusy(true)
    setProblem(null)
    try {
      const found = await geocode(query)
      setHits(found)
      if (found.length === 0) setProblem('Nothing matched that address.')
    } catch (cause: unknown) {
      /*
       * A geocoder that is not configured is not an error somebody can act
       * on by typing more, so it says what it is rather than "nothing
       * matched" — and **Drop a pin** is still there, which is the point of
       * having two ways out.
       */
      setProblem(cause instanceof ApiError
        ? cause.message
        : 'The address search is not available right now.')
    } finally {
      setBusy(false)
    }
  }

  /* `min-h-[560px]`: the same height the editor's canvas takes, so the tab
     does not change shape the moment a pin is saved. */
  return (
    <div className="flex min-h-[560px] flex-1 flex-col p-4">
      <MapCanvasFrame>
        <MapPanel
          tone="warn"
          icon={<IconPin size={20} />}
          title={`We couldn’t place ${address}`}
          body={
            'Search the address again, or drop a pin on the map. The pin is saved to the'
            + ' property and used for Navigate too.'
          }
          actions={(
            <>
              <label className="flex w-full min-w-0 flex-col gap-1.5">
                <span className="text-[12px] font-semibold text-text-body">Address</span>
                <TextInput
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') void search() }}
                  disabled={!canEdit}
                  placeholder="1200 Front St, Harrisburg, PA"
                />
              </label>

              {problem && <p className="m-0 text-cell text-danger">{problem}</p>}

              {hits && hits.length > 0 && (
                <ul className={cn(
                  'm-0 flex max-h-[168px] list-none flex-col overflow-y-auto rounded-field',
                  'border border-line p-0',
                )}
                >
                  {hits.map((hit) => (
                    <li key={`${hit.lng},${hit.lat}`}>
                      <button
                        type="button"
                        disabled={!canEdit || busy}
                        onClick={() => { void onSave({ lng: hit.lng, lat: hit.lat }) }}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2.5 text-left text-cell',
                          'hover:bg-hover disabled:opacity-50',
                        )}
                      >
                        <IconPin size={14} />
                        {hit.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <PanelActions>
                <Button
                  disabled={!canEdit}
                  /* PR 3 hangs the real drop-a-pin interaction off this — it
                     needs a map to click on. Until then it says what it is
                     waiting for rather than pretending to work. */
                  onClick={() => setProblem(
                    'Dropping a pin by hand comes with the map editor. Search the address'
                    + ' for now — the result is saved to the property the same way.')}
                >
                  <IconPin size={15} />
                  Drop a pin
                </Button>
                <Button variant="primary" disabled={busy || !canEdit} onClick={() => void search()}>
                  <IconSearch size={15} />
                  {busy ? 'Searching…' : 'Search'}
                </Button>
              </PanelActions>
            </>
          )}
        />
      </MapCanvasFrame>
    </div>
  )
}
